// @ts-ignore
import { getField, updateField } from 'vuex-map-fields'
import pick from 'lodash/pick'
import dayjs from 'dayjs'
import { FeatureModel } from '@/models/subscription/FeatureModel'
import AddonCycleEnum from '@/models/subscription/AddonCycleEnum'
import FormsService from '@/services/forms-service'
import SubscriptionAPIs from '@/API/subscription/subscription-api'
import StorageService from '@/services/storage-service'
import MarketingService, { MARKETING_EVENTS } from '@/services/marketing-service'

export default {
  namespaced: true,
  state: {
    plan: null,
    cycle: 'yearly',
    features: [],
    coupon: {},
    payment_method: 'visa',
    image: '',
    token: '',
    billing_address: {
      first_name: '',
      last_name: '',
      email: '',
    },
    preSelectedPlan: false,
    succeed: false,
    upgradeModal: false,
    source: '',
  },
  getters: {
    // Add the `getField` getter to the
    // `getters` of your Vuex store instance.
    getField,
    isAddonHasCount: () => (slug: string) => ['subcashier', 'message_packs'].includes(slug),
    monthlyPrice: (state: any) => {
      const packagePrice = state.plan?.monthly_price || 0
      const monthlyAddons = state.features.filter((feature: any) => [AddonCycleEnum.BOTH, AddonCycleEnum.MONTHLY].includes(feature.cycle))
      const addonsPrice = monthlyAddons.reduce((prev: any, curr: any) => prev + ((curr.monthly_price || curr.price) * (curr.count || 1)), 0)
      return packagePrice + addonsPrice
    },
    totalMonthlyPrice: (state: any, getters: any) => {
      const nonMonthlyAddons = state.features.filter((feature: any) => [AddonCycleEnum.YEARLY, AddonCycleEnum.ONE_TIME].includes(feature.cycle))
      const addonsPrice = nonMonthlyAddons.reduce((prev: any, curr: any) => prev + (curr.yearly_price || curr.price), 0)
      return getters.monthlyPrice + addonsPrice
    },
    yearlyPrice: (state: any) => {
      const packagePrice = state.plan?.yearly_price || 0
      const yearlyAddons = state.features.filter((feature: any) => [AddonCycleEnum.BOTH, AddonCycleEnum.YEARLY].includes(feature.cycle))
      const monthlyAddons = state.features.filter((feature: any) => feature.cycle === AddonCycleEnum.MONTHLY)
      const addonsPrice = yearlyAddons.reduce((prev: any, curr: any) => prev + (curr.yearly_price * (curr.count || 1)), 0)
      const monthlyAddonsPrice = monthlyAddons.reduce((prev: any, curr: any) => prev + ((curr.monthly_price || curr.price) * 12), 0)
      return packagePrice + addonsPrice + monthlyAddonsPrice
    },
    totalYearlyPrice: (state: any, getters: any) => {
      const oneTimeAddons = state.features.filter((feature: any) => feature.cycle === AddonCycleEnum.ONE_TIME)
      const oneTimeAddonsPrice = oneTimeAddons.reduce((prev: any, curr: any) => prev + (curr.yearly_price || curr.price), 0)
      return getters.yearlyPrice + oneTimeAddonsPrice
    },
    canUpgrade: (state: any) => (state.plan?.upgrade_price ?? -1) >= 0 && state.cycle !== 'monthly',
    isUpgrade: (state: any) => (state.plan?.upgrade_price ?? -1) >= 0 && state.cycle === 'yearly',
    upgradeAmount: (state: any, getters: any) => (getters.isUpgrade ? state.plan?.yearly_price - state.plan?.upgrade_price : 0),
    subtotal: (state: any, getters: any) => (state.cycle === 'monthly' ? getters.totalMonthlyPrice : getters.totalYearlyPrice),
    discount: (state: any) => state.coupon?.value ?? 0,
    discountedValue: (state: any, getters: any) => getters.subtotal * (getters.discount / 100) ?? 0,
    total: (state: any, getters: any) => {
      const subtotal = getters.subtotal - getters.upgradeAmount
      return (getters.discount || getters.upgradeAmount) ? (subtotal - (subtotal * (getters.discount / 100))) : subtotal
    },
    billingAddress: (state: any) => state.billing_address,
    purchaseAddons: (state: any) => state.features.length > 0,
    forUpgrade: (state: any, getters: any) => !getters.purchaseAddons,
    showPlans: (state: any, getters: any, rootState: any, rootGetters: any) => rootGetters['subscription/isTrial'],
    hideFirstStep: (state: any) => state.preSelectedPlan && !state.features.length,
    canGetExtraFree: (state: any, getters: any, rootState: any, rootGetters: any) => rootGetters['subscription/canGetExtraFree'] && state.plan?.id,
    purchaseSucceed: (state: any) => state.succeed,
    subscribeToFreeOnly: (state: any) => state.plan?.type === 'free' && !state.features.length,
  },
  mutations: {
    // Add the `updateField` mutation to the
    // `mutations` of your Vuex store instance.
    updateField,
    RESET(state: any) {
      state.plan = null
      state.cycle = 'yearly'
      state.features = []
      state.coupon = ''
      state.payment_method = 'visa'
      state.image = ''
      state.token = ''
      state.billing_address = {}
      state.preSelectedPlan = false
      state.succeed = false
      state.upgradeModal = false
      state.source = ''
    },
    SET_BILLING_INFO(state: any, billingInfo: any) {
      state.billing_address = { ...state.billing_address, ...billingInfo }
    },
    SET_PLAN(state: any, plan: any) {
      state.plan = plan
      state.preSelectedPlan = !!plan
    },
    SET_FEATURES(state: any, features: any) {
      state.features = features || []
    },
    SET_CYCLE(state: any, cycle: any) {
      state.cycle = cycle || []
    },
    SET_STATUS(state: any, succeed: boolean) {
      state.succeed = succeed
    },
    SET_UPGRADE_MODAL(state: any, upgradeModal: boolean) {
      state.upgradeModal = upgradeModal
    },
  },
  actions: {
    INIT_PURCHASE(context: any, {
      planId, plan, addonSlugs, addonSlug, cycle, upgradeModal, source, addonLimit,
    }: any) {
      context.commit('RESET')
      context.commit('SET_BILLING_INFO', context.rootGetters['auth/user']?.billing_address)
      const purchaseSummary: any = {}

      if (plan || planId) {
        const packages = context.rootGetters['pricing/regularPackages']
        const selectedPlan = plan ?? packages.find((_package: any) => _package.id === planId)
        context.commit('SET_PLAN', selectedPlan)
        purchaseSummary.plan = selectedPlan
      }

      if (addonSlugs || addonSlug) {
        const slugs = addonSlug ? [addonSlug] : addonSlugs
        const addons = context.rootGetters['pricing/addons']
        const selectedAddons = addons.filter((addon: any) => slugs.includes(addon.slug)).map((addon: any) => ({ ...addon, countable: context.getters.isAddonHasCount(addon.slug), count: Math.max(((context.getters.isAddonHasCount(addon.slug) ? addonLimit : 1) || 1), 1) }))
        context.commit('SET_FEATURES', selectedAddons)
        purchaseSummary.addons = selectedAddons
      }

      if (cycle) {
        context.commit('SET_CYCLE', cycle)
        purchaseSummary.cycle = cycle
      }

      if (upgradeModal) {
        context.commit('SET_UPGRADE_MODAL', upgradeModal)
      }

      context.commit('updateField', { path: 'source', value: source })

      MarketingService.triggerEvent(MARKETING_EVENTS.PURCHASE_BEGIN, 'purchase', 'user start purchase', purchaseSummary, 1000)
    },
    async PURCHASE({
      state, getters, rootGetters, commit,
    }: any, route: any) {
      const purchasePackage = !!state.plan?.id
      const addonCycle = state.cycle === 'monthly' ? AddonCycleEnum.MONTHLY : AddonCycleEnum.YEARLY
      const payload: any = {
        package_id: state.plan?.id,
        cycle: state.cycle,
        billing: state.cycle,
        total_price: getters.total,
        features: JSON.stringify(state.features.map((feature: FeatureModel) => ({ id: feature.id, cycle: feature.cycle === AddonCycleEnum.BOTH ? addonCycle : feature.cycle, limit: feature.count || 1 }))),
        payment_method: state.payment_method,
        branch: rootGetters['branches/currentBranchId'],
        billing_address: JSON.stringify(state.billing_address),
        token: state.token,
      }
      if (!purchasePackage) {
        delete payload.package_id
      }
      if (state.coupon?.code) {
        payload.coupon_code = state.coupon?.code
      }
      const formData = FormsService.convertObjectToFormData(payload)
      const isWiretransfer = state.payment_method === 'wiretransfer'

      if (isWiretransfer) {
        formData.append('image', state.image, 'receipt.jpg')
      }

      let response = null
      if (getters.isUpgrade) {
        formData.append('_method', 'put')
        response = await SubscriptionAPIs.upgrade(formData)
      } else if (purchasePackage) {
        response = await SubscriptionAPIs.purchasePackage(formData)
      } else {
        response = await SubscriptionAPIs.purchaseAddon(formData)
      }
      commit('auth/UPDATE_USER', { billing_address: state.billing_address }, { root: true })

      const purchaseSummary: any = { ...pick(state, ['plan', 'features', 'payment_method', 'cycle', 'source', 'coupon']), total: getters.total, discount: getters.discount }
      if (!['wiretransfer', 'fawry'].includes(state.payment_method)) {
        StorageService.setItem('okmenu_last_purchase', { ...purchaseSummary, route }, dayjs().add(60, 'minutes').toDate())
      }

      const paymentUrl = response?.payment_url || response?.url

      if (!isWiretransfer && !paymentUrl) {
        commit('SET_STATUS', true)
        StorageService.setItem('okmenu_last_purchase', { ...purchaseSummary, route, eventTriggered: true }, dayjs().add(60, 'minutes').toDate())
        await MarketingService.triggerEvent(MARKETING_EVENTS.PURCHASE_COMPLETE, 'Purchase', 'user purchased', purchaseSummary)
      }

      StorageService.removeItem('okmenu_coupon')

      return response
    },
  },
}
